import React, { useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import {
  SettingsApiDataSource,
  SettingsApiError,
} from "../../data/datasources/SettingsApiDataSource";
import type { BankSettingsDTO } from "../../data/models/CompanySettingsDTO";

/**
 * The message worth showing the user.
 *
 * The settings envelope carries "Validation failed" as its message and the
 * useful sentence ("must be between 50 and 100") in `errors`, keyed by field.
 * Showing the envelope alone tells the user something is wrong but not what,
 * which is exactly the case this card exists to explain.
 */
const readError = (error: unknown, fallback: string): string => {
  if (error instanceof SettingsApiError) {
    const fieldMessage = error.fieldErrors?.duplicate_match_threshold;
    if (fieldMessage) {
      return fieldMessage;
    }
  }

  return error instanceof Error && error.message ? error.message : fallback;
};

/**
 * DuplicateThresholdCard — ZOUR-87.
 *
 * One number: how alike two narrations have to be before a re-uploaded row
 * with the same date and amount as an already-imported one is flagged as a
 * possible duplicate and left unticked on the statement review screen.
 *
 * It lives on the Banks panel because that is where a CA goes when statements
 * are behaving oddly, and it is company-wide because it is a judgement about
 * this client's bank and extraction quality, not about one account.
 *
 * Server-driven: the band, the default and the help text all come from
 * `GET /api/v1/settings/banks`, so this input cannot drift from what the
 * validator will accept.
 */
export const DuplicateThresholdCard: React.FC = () => {
  const [settings, setSettings] = useState<BankSettingsDTO | null>(null);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const api = new SettingsApiDataSource();

    api
      .fetchBankSettings(controller.signal)
      .then((data) => {
        setSettings(data);
        setValue(String(data.duplicate_match_threshold.effective));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(readError(e, "Could not load the duplicate setting."));
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  if (loading || !settings) {
    return null;
  }

  const field = settings.duplicate_match_threshold;
  const current = field.value ?? field.default;
  const dirty = value.trim() !== String(current);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const parsed = Number(value.trim());
      const data = await new SettingsApiDataSource().saveBankSettings({
        duplicate_match_threshold: Number.isFinite(parsed) ? parsed : null,
      });
      setSettings(data);
      setValue(String(data.duplicate_match_threshold.effective));
      setSaved(true);
    } catch (e: unknown) {
      setError(readError(e, "Could not save the setting."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-5">
      <div className="flex flex-col gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {field.label}
        </span>
        <span className="text-body font-body text-subtext-color">{field.help}</span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="number"
          min={field.min}
          max={field.max}
          step={1}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSaved(false);
          }}
          className="w-[110px] rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font [font-variant-numeric:tabular-nums] focus:border-brand-primary focus:outline-none"
        />
        <span className="text-caption font-caption text-subtext-color">
          % · between {field.min} and {field.max} · default {field.default}
          {field.value === null && " (currently using the default)"}
        </span>
        <div className="grow" />
        <Button loading={saving} disabled={!dirty} onClick={handleSave}>
          Save
        </Button>
      </div>

      {error && (
        <span className="text-caption font-caption text-error-700">{error}</span>
      )}
      {saved && !error && (
        <span className="text-caption font-caption text-success-700">
          Saved. This applies to the next statement you review — statements
          already approved are not re-graded.
        </span>
      )}
    </section>
  );
};

export default DuplicateThresholdCard;
